import { API_ENDPOINTS, apiService } from "../api";
import { isMockApiFlag } from "../apiMode";
import { getDataStudioColumnDetails, updateDataStudioColumnDetails } from "./dataStudioColumnDetailsApi";
import type {
  DataStudioRecommendation,
  GetDataStudioRecommendationsRequest,
  GetDataStudioRecommendationsResponse,
} from "./dataStudioRecommendations.types";

const isMockEnabled = (): boolean => isMockApiFlag(process.env.REACT_APP_DATA_STUDIO_API);

export const buildDataStudioRecommendationsUrl = (connectionId: string, assetId: string): string =>
  API_ENDPOINTS.dataStudio.assetRecommendations(connectionId, assetId);

/** Mock mode: one generic finding built from ids only, no tenant values. */
export const createMockDataStudioRecommendations = (assetId: string): GetDataStudioRecommendationsResponse => ({
  asset_id: assetId,
  profile_hash: "mock",
  profiled_at: null,
  generated_at: new Date(0).toISOString(),
  status: "ready",
  items: [
    {
      id: "mock-nulls",
      kind: "nulls",
      columns: [{ asset_id: assetId, column_id: "mock-column", name: "column" }],
      severity: "medium",
      title: "Many values are empty",
      message: "A share of this column is empty. Add a note so readers know how to treat it.",
      evidence: {},
      action: { type: "add_note", enabled: true, label: "Save as note", payload: {}, disabled_reason: null },
    },
  ],
});

export const getDataStudioRecommendations = async ({
  connectionId,
  assetId,
}: GetDataStudioRecommendationsRequest): Promise<GetDataStudioRecommendationsResponse> => {
  if (isMockEnabled()) return createMockDataStudioRecommendations(assetId);
  return apiService.get<GetDataStudioRecommendationsResponse>(buildDataStudioRecommendationsUrl(connectionId, assetId));
};

/** The note text a "Save as note" action stores: the finding's title and message. */
export const buildRecommendationNote = (item: DataStudioRecommendation): string =>
  `${item.title}. ${item.message}`.trim();

/**
 * `add_note` appends to the column's existing notes through the column PATCH.
 * PATCH replaces `notes`, so the current list is read first.
 */
export const saveRecommendationAsNote = async (
  connectionId: string,
  item: DataStudioRecommendation,
): Promise<void> => {
  const column = item.columns[0];
  if (!column) throw new Error("This recommendation has no column to attach a note to.");
  const identity = { connectionId, assetId: column.asset_id, columnId: column.column_id };
  const note = buildRecommendationNote(item);
  const current = await getDataStudioColumnDetails(identity);
  if (current.notes.includes(note)) return;
  await updateDataStudioColumnDetails({ ...identity, notes: [...current.notes, note] });
};
